// import moment from 'moment';
import React, { useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';
import { subscribeListener, unsubscribeListener } from 'redux/ably/sagas';
import { colors, styled } from '../../../assets/theme';
import configs, { IDisplaySetting } from '../../../redux/chart-iq/chart/config'

export interface IQuote {
  p?: number
  nc?: number
  t?: Date
}


export const ACTION_COLORS = {
  BUY: {
    bgColor: colors.accents.good.base,
    fontColor: colors.accents.good.base,
  },
  SELL: {
    bgColor: colors.accents.bad.base,
    fontColor: colors.accents.bad.base,
  },
};


interface IActiveRotationModel {
  a?: IQuote
  tgt?: number,
  action?: string,
  pct?: number,
  p100?: number,
  p99?: number,
  p98?: number,
  p96?: number,
  pmax?: number,
  start_time?: string,
  watch?: number,
  model?: string,
  price?: number | null,
  trigger_time?: string,
}

const ActiveRotation: React.FunctionComponent<{active: IActiveRotationModel, symbol: string}> = ({ active, symbol }) => {
  const [id] = useState(Math.floor(Math.random() * 10 ** 18));
  const [quote, setQuote] = useState<IQuote>();
  const [modelpts, setModelpts] = useState<number>()
  const [displaySettings, setDisplaySettings] = useState<IDisplaySetting>({
    decimalPlaces: 2,
    priceConverter: (price) => price,
    pricingTerm: 'pts',
    type: 'market',
  })

  useEffect(() => {
    if (active && active.model) {
      setModelpts(+(active.model.split('_static')[1].replace('_', '.')))
    }
  }, [active])

  useEffect(() => {
    if (configs.displaySettings[symbol.toLowerCase()]) {
      setDisplaySettings(configs.displaySettings[symbol.toLowerCase()])
    }
    subscribeListener(`sym:${symbol}:tick`, id, ({ data }) => {
      setQuote(data);
    });
    return () => {
      unsubscribeListener(`sym:${symbol}:tick`, id);
    }
  }, [id, symbol]);
  const { action } = active
  if (!quote || !action || !ACTION_COLORS[action]) { return <div /> }
  const { p } = quote
  if (!p) { return <div /> }
  return (
    <ActiveRotationContainer bgColor={ACTION_COLORS[action].bgColor}>
      <div>
        <div>
          <NumberFormat value={modelpts} displayType="text" fixedDecimalScale={true} decimalScale={displaySettings.decimalPlaces} />
          {`${displaySettings.pricingTerm} @`}
          <NumberFormat value={active.watch} displayType="text" fixedDecimalScale={true} decimalScale={1} />%
        </div>
        <div><span>{active.action}</span><span>{symbol}</span></div>
      </div>
      <DataItem fontColor={ACTION_COLORS[action].fontColor}>
        <div>PCT</div>
        <div><NumberFormat value={active.pct} displayType="text" fixedDecimalScale={true} decimalScale={displaySettings.decimalPlaces} />%</div>
      </DataItem>
      <DataItem fontColor={ACTION_COLORS[action].fontColor}>
        <div>P96</div>
        <div><NumberFormat value={active.p96} displayType="text" fixedDecimalScale={true} decimalScale={displaySettings.decimalPlaces} /></div>
      </DataItem>
      <DataItem fontColor={ACTION_COLORS[action].fontColor}>
        <div>P98</div>
        <div><NumberFormat value={active.p98} displayType="text" fixedDecimalScale={true} decimalScale={displaySettings.decimalPlaces} /></div>
      </DataItem>
      <DataItem fontColor={ACTION_COLORS[action].fontColor}>
        <div>P99</div>
        <div><NumberFormat value={active.p99} displayType="text" fixedDecimalScale={true} decimalScale={displaySettings.decimalPlaces} /></div>
      </DataItem>
      <DataItem fontColor={ACTION_COLORS[action].fontColor}>
        <div>P100</div>
        <div><NumberFormat value={active.p100} displayType="text" fixedDecimalScale={true} decimalScale={displaySettings.decimalPlaces} /></div>
      </DataItem>
      <DataItem fontColor={ACTION_COLORS[action].fontColor}>
        <div>Current</div>
        <NumberFormat value={displaySettings.priceConverter(p)} displayType="text" fixedDecimalScale={true} decimalScale={displaySettings.decimalPlaces} />
      </DataItem>
      <DataItem fontColor={ACTION_COLORS[action].fontColor}>
        <div>Target</div>
        <div><NumberFormat value={active.tgt} displayType="text" fixedDecimalScale={true} decimalScale={displaySettings.decimalPlaces} /></div>
      </DataItem>
      <DataItem fontColor={ACTION_COLORS[action].fontColor}>
        <div>pMax</div>
        <div><NumberFormat value={active.pmax} displayType="text" fixedDecimalScale={true} decimalScale={displaySettings.decimalPlaces} />%</div>
      </DataItem>
    </ActiveRotationContainer>
  );
};

export const ActiveRotations: React.FunctionComponent<{id: number}> = ({ id }) => {
  const [actives, setActives] = useState<IActiveRotationModel[]>([]);
  useEffect(() => {
    subscribeListener('rotations:actives', id, ({ data }) => {
      setActives(data);
    });
    return () => {
      unsubscribeListener('rotations:actives', id);
    }
  }, [id]);
  if (!actives || actives.length === 0) {
    return (
      <ActiveRotationsContainer>
        <div style={{ height: '60px', display: 'grid', justifyContent: 'center', alignContent: 'center' }}>
          <span style={{ fontSize: '1.5em' }}>No active rotations</span>
        </div>
      </ActiveRotationsContainer>
    );
    }
  return (
    <ActiveRotationsContainer>
    {
      actives.map(active => {
        if (active.model) {
          const symbol = active.model.split('_')[0].toUpperCase();
          return <ActiveRotation key={active.trigger_time} active={active} symbol={symbol} />;
        }
        return <div key={active.trigger_time} />
      })
    }
    </ActiveRotationsContainer>
  );
};

const DataItem = styled.div<{fontColor: string}>`
  display: grid;
  justify-items: center;
  grid-gap: 0.5em;
> :first-child {
  color: ${({ fontColor }) => fontColor};
  font-size: smaller;
}
`;

const ActiveRotationsContainer = styled.div`
  display:grid;
  grid-auto-flow: row;
  grid-gap: 0.5em;
  border: solid 1px ${({ theme }) => theme.core.offBackground};
  > :nth-child(2n+1) {
    background: ${({ theme }) => theme.core.darkBackground};
  }
`;

export const ActiveRotationContainer = styled.div<{bgColor: string}>`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: repeat(10, 1fr);
  align-items: center;

  > :first-child {
    padding: 0.5em;
    color: ${colors.spectrum.offblack.D3};
    background: ${({ bgColor }) => bgColor};
    font-size: larger;
    display: grid;
    grid-auto-flow: row;
    grid-gap: 0.5em;
    grid-column: span 2;
    > :last-child {
      font-weight: 600;
      font-size: larger;
      display: grid;
      grid-auto-flow: column;
      grid-gap: 1em;
      justify-content: start;
    }
  }
`;
